//@flow

import {useCallback, useEffect, useRef, useState} from 'react';

import type {RequestStatus} from '../../entities/request';

type UseEventSourceOptions = {
  onMessage: (e: MessageEvent) => void,
  onError: (e: Event) => void,
  onOpen?: (e: any) => void,
};

type EventSourceState = 'connecting' | 'open' | 'closed' | 'error';

export function useEventSource(url: string, options: UseEventSourceOptions) {
  const [sourceState, setSourceState] = useState<EventSourceState>('closed');
  const optionsRef = useRef(options);

  useEffect(() => {
    const {onError, onMessage, onOpen} = optionsRef.current;
    setSourceState('connecting');
    const eventSource = new EventSource(url);

    eventSource.addEventListener('message', onMessage);

    const handleError = (event: Event) => {
      setSourceState('error');
      if (onError) onError(event);
    };

    eventSource.addEventListener('error', handleError);

    const handleOpen = (event: any) => {
      setSourceState('open');
      if (onOpen) onOpen(event);
    };
    eventSource.addEventListener('open', handleOpen);

    return () => {
      eventSource.close();
      setSourceState('closed');
      eventSource.removeEventListener('message', onMessage);
      eventSource.removeEventListener('error', handleError);
      eventSource.removeEventListener('open', handleOpen);
    };
  }, [url]);

  return {
    connecting: sourceState === 'connecting',
    open: sourceState === 'open',
  };
}

export function usePromise<T>(
  promise: (...args: any) => Promise<T>,
  debug: boolean = false
): {
  status: RequestStatus | null,
  reqHandler: (...args: any) => void,
  data: T | null,
} {
  const [status, setStatus] = useState<RequestStatus | null>(null);
  const [data, setData] = useState<T | null>(null);

  const reqHandler = useCallback(
    (...args: any) => {
      promise(...args)
        .then(el => {
          if (el) setData(el);
        })
        .catch(err => {
          // if (debug) console.log(err);
          setStatus('ERROR');
        });
    },
    [promise]
  );

  return {status, reqHandler, data};
}
